import React, { useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { makeStyles } from '@material-ui/styles';
import {
	Button, TextField, Typography, Grid, Select, Card,
	CardHeader, CardContent, Divider, Accordion, AccordionSummary,
	AccordionDetails
} from '@material-ui/core';
import L from 'leaflet';
import { onUpdateLegend } from 'components/Map/actions/MapActions';
import CreateIcon from '@material-ui/icons/Create';
import EmailIcon from '@material-ui/icons/Email';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import EditIcon from '@material-ui/icons/Edit';
import { Page, Header, Map, SelectField, Label, SearchBar, FiltersDrawer } from 'components';
import { SetFormState, GetLayer3Failure, GetLayer3DriveTest } from '../../../../../../../../../../actions/VoiceActions';

const useStyles = makeStyles((theme) => ({
	root: {},
	content: {
		padding: 0,
	},
	inner: {
		minWidth: 700,
	},
	nameCell: {
		display: 'flex',
		alignItems: 'center',
	},
	avatar: {
		height: 42,
		width: 42,
		marginRight: theme.spacing(1),
	},
	actions: {
		padding: theme.spacing(1),
		justifyContent: 'flex-end',
	},
	primaryButton: {
		color: theme.palette.primary.main
	},
	errorButton: {
		color: theme.palette.error.main
	},
	infoButton: {
		color: theme.palette.info.main
	}
}));

const Layer3Map = (props) => {
	const { className, data, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));

	const maps = useSelector(state => state.mapReducer.maps);
	const mapOne = maps.filter(item => item.id === 'failures-map-layer3')[0];
	const formState = useSelector((state) => state.voiceReducer.formState);

	const tableId = useSelector((state) => state.voiceReducer.tableId);
	const filenameMoc = useSelector((state) => state.voiceReducer.filenameMoc);
	const filenameMtc = useSelector((state) => state.voiceReducer.filenameMtc);
	const failureSide = useSelector((state) => state.voiceReducer.failureSide);
	const initialTimestamp = useSelector((state) => state.voiceReducer.initialTimestamp);

	const layer3Failure = useSelector((state) => state.voiceReducer.layer3Failure);
	const layer3DriveTest = useSelector((state) => state.voiceReducer.layer3DriveTest);

	const failureLockOn = useSelector((state) => state.voiceReducer.failureLockOn);
	const userId = useSelector(state => state.session.user.id);

	const kpisNames = [
		{ value: 'rsrp', text: 'RSRP' },
		{ value: 'rsrq', text: 'RSRQ' },
		{ value: 'sinrVoice', text: 'SINR' }
	];

	const makeDetailsTable = (item) => {
		const table = `<table class="table table-striped">
							<tr>
							<th>Failure Id: </th>
							<td>${item.failureId}</td>
							</tr>
							<tr>
							<th>Timestamp: </th>
							<td>${item.initialTimestamp}</td>
							</tr>
							<tr>
							<th>Latitude: </th>
							<td>${item.latitude}</td>
							</tr>
							<tr>
							<th>Longitude: </th>
							<td>${item.longitude}</td>
							</tr>
						</table>`;
		return table;
	};

	const makeDetailsTableKpis = (item) => {
		const table = `<table class="table table-striped">
						<tr>
						<th>Level: </th>
						<td>${item.kpi}</td>
						</tr>
						<tr>
						<th>Latitude: </th>
						<td>${item.latitude}</td>
						</tr>
						<tr>
						<th>Longitude: </th>
						<td>${item.longitude}</td>
						</tr>
					</table>`;
		return table;
	};

	const _addLayerFailures = (map, layerData, type, fitBounds) => {
		map.RemoveLayerById('failures');
		const markers = [];

		const voiceIconBlocked = L.icon({
			iconUrl: '/images/failuresIcons/eventsvoicecallattempt_o2.png',
			iconSize: [35, 35], // size of the icon
			iconAnchor: [16, 35], // point of the icon which will correspond to marker's location
			popupAnchor: [0, -35] // point from which the popup should open relative to the iconAnchor
		});

		const voiceIconBlockedQoe = L.icon({
			iconUrl: '/images/failuresIcons/eventsvoiceblockcallQoe_o2.png',
			iconSize: [35, 35], // size of the icon
			iconAnchor: [16, 35], // point of the icon which will correspond to marker's location
			popupAnchor: [0, -35] // point from which the popup should open relative to the iconAnchor
		});

		const voiceIconDropped = L.icon({
			iconUrl: '/images/failuresIcons/eventsvoicedropcall_o2.png',
			iconSize: [35, 35], // size of the icon
			iconAnchor: [16, 35], // point of the icon which will correspond to marker's location
			popupAnchor: [0, -35] // point from which the popup should open relative to the iconAnchor
		});

		const voiceIconDroppedQoe = L.icon({
			iconUrl: 'images/failuresIcons/eventsvoicedropcallQoe_o2.png',
			iconSize: [35, 35], // size of the icon
			iconAnchor: [16, 35], // point of the icon which will correspond to marker's location
			popupAnchor: [0, -35] // point from which the popup should open relative to the iconAnchor
		});

		const packetIcondrop = L.icon({
			iconUrl: 'images/failuresIcons/eventspacketdropcall_o2.png',
			iconSize: [35, 35], // size of the icon
			iconAnchor: [16, 35], // point of the icon which will correspond to marker's location
			popupAnchor: [0, -32] // point from which the popup should open relative to the iconAnchor
		});

		if (!_.isEmpty(layerData)) {
			layerData.forEach(item => {
				if (type.includes('voice')) {
					if (item.failureType === 'Blocked Call') {
						const marker = L.marker([item.latitude, item.longitude], { title: item.failureId, icon: voiceIconBlocked });
						markers.push(marker.bindPopup(`<br>${makeDetailsTable(item)}<br>`, { autoPan: false }));
					} else if (item.failureType === 'QoE Call Setup Time') {
						const marker = L.marker([item.latitude, item.longitude], { title: item.failureId, icon: voiceIconBlockedQoe });
						markers.push(marker.bindPopup(`<br>${makeDetailsTable(item)}<br>`, { autoPan: false }));
					} else if (item.failureType === 'Dropped Call') {
						const marker = L.marker([item.latitude, item.longitude], { title: item.failureId, icon: voiceIconDropped });
						markers.push(marker.bindPopup(`<br>${makeDetailsTable(item)}<br>`, { autoPan: false }));
					} else {
						const marker = L.marker([item.latitude, item.longitude], { title: item.failureId, icon: voiceIconDroppedQoe });
						markers.push(marker.bindPopup(`<br>${makeDetailsTable(item)}<br>`, { autoPan: false }));
					}
				} else {
					const marker = L.marker([item.latitude, item.longitude], { title: item.failureId, icon: packetIcondrop });
					markers.push(marker.bindPopup(`<br>${makeDetailsTable(item)}<br>`, { autoPan: false }));
				}

				// const marker = L.marker([item.latitude, item.longitude], { title: item.failureId, icon: voiceIconBlocked });
				// markers.push(marker.bindPopup(`<br>${makeDetailsTable(item)}<br>`, { autoPan: false }));
			});

			const layer = L.layerGroup(markers, { id: 'failures' });
			layer.addTo(map.mapContext.map);
			const group = new L.featureGroup(markers);
			if (fitBounds) {
				map.mapContext.map.fitBounds(group.getBounds());
			}
		}
		// dispatch(onUpdateLegend({ id: map.mapContext.id, newLegend: legend }));
	};

	const _addLayerKpi = (map, layerData, legend, fitBounds) => {
		map.RemoveLayerById('kpi');
		const markers = [];

		if (!_.isEmpty(layerData)) {
			layerData.forEach(item => {
				const marker = L.circle([item.latitude, item.longitude], {
					color: item.color,
					fillColor: item.color,
					fillOpacity: 0.8,
					radius: 10
				});
				markers.push(marker.bindPopup(`<br>${makeDetailsTableKpis(item)}<br>`, { autoPan: false }));
			});

			const layer = L.layerGroup(markers, { id: 'kpi' });
			layer.addTo(map.mapContext.map);
			const group = new L.featureGroup(markers);
			if (fitBounds) {
				map.mapContext.map.fitBounds(group.getBounds());
			}
		}

		dispatch(onUpdateLegend({ id: map.mapContext.id, newLegend: legend }));
	};

	const _handleChangeKpi = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				layer3Kpi: event.target.value,
			}
		});

		dispatch(GetLayer3DriveTest(userId, event.target.value, filenameMoc, filenameMtc, failureSide, initialTimestamp));
	};

	useEffect(() => {
		dispatch(GetLayer3Failure(tableId));
	}, []);

	useMemo(() => {
		if (mapOne) {
			_addLayerFailures(mapOne.control, layer3Failure, 'voice', true);
		}
	}, [layer3Failure]);

	useMemo(() => {
		if (mapOne) {
			_addLayerKpi(mapOne.control, layer3DriveTest.driveTestPoints, layer3DriveTest.DriveTestLegend || '', true);
		}
	}, [layer3DriveTest]);

	return (
		<div className={clsx(classes.container)}>
			<Grid container spacing={4}>
				<Grid item md={3} xs={3}>
					<SelectField
						id="layer3Tech"
						name="layer3Tech"
						hintText="Technology"
						defaultValue=""
						menuItems={[{ value: '4g', text: '4G' }]}
						// onChange={_handleChange}
						value="4g"
					/>
				</Grid>
				<Grid item md={3} xs={3}>
					<SelectField
						id="layer3Kpi"
						name="layer3Kpi"
						hintText="Kpi"
						defaultValue=""
						menuItems={kpisNames}
						onChange={_handleChangeKpi}
					// value="4g"
					/>
				</Grid>
			</Grid>
			<Map id="failures-map-layer3" scale height="30vh" />
		</div>
	);
};

Layer3Map.propTypes = {
	className: PropTypes.string,
	data: PropTypes.array.isRequired,
	height: PropTypes.string
};

export default Layer3Map;
